import NewOfferBg from "@static/freelancer/new-offer-bg.svg";

import { Box, Button } from "@braintrust/braintrust-ui-components";
import { PendingFreelancerActionButton } from "@js/apps/freelancer/components/pending-freelancer-action-button";
import { useUserNeedsIdentityVerification } from "@js/apps/identity-verification";
import { BidButton } from "@js/apps/jobs/apps/bids";
import { openChoosePayoutMethodModal } from "@js/apps/withdrawal/components/choose-payout-method-modal";
import type { FreelancerBidListItem, Job } from "@js/types/jobs";
import { assertUnreachable } from "@js/utils";
import { dateFromNow } from "@js/utils/date";

import {
  BrowseOpenJobs,
  RespondToOfferButton,
  ReviewContractButton,
  ScheduleInterviewButton,
  StartAIInterviewButton,
  ViewApplicationButton,
} from "../../job-action-buttons/components";
import { JobStatusBox } from "../components/job-status-box";

import styles from "../styles.module.scss";

type StatusForFreelancerProps = {
  job: Job | null;
  bid?: FreelancerBidListItem;
  className?: string;
  longTile?: boolean;
  statusForFreelancer?: EnumType<typeof ENUMS.JobFreelancerStatus>;
};

export const StatusForFreelancer = ({
  job,
  bid,
  statusForFreelancer,
  ...props
}: StatusForFreelancerProps) => {
  const { userFailedIdentityVerification } = useUserNeedsIdentityVerification();
  const appliedDateFromNow = dateFromNow(bid?.created);
  if (!job || !statusForFreelancer) {
    return null;
  }

  switch (statusForFreelancer) {
    case ENUMS.JobFreelancerStatus.ACCEPTING_APPLICATIONS:
      return <ApplyTile job={job} bid={bid} {...props} />;
    case ENUMS.JobFreelancerStatus.INVITED_BY_CLIENT:
      return (
        <InvitedTile job={job} bid={bid} label="Invited by client" {...props} />
      );
    case ENUMS.JobFreelancerStatus.INVITED_BY_MATCHER:
      return (
        <InvitedTile
          job={job}
          bid={bid}
          label="Invited by matcher"
          {...props}
        />
      );
    case ENUMS.JobFreelancerStatus.PENDING_APPROVAL:
    case ENUMS.JobFreelancerStatus.PENDING_VERIFICATION:
      return <PendingTile jobId={job.id} bidId={bid?.id} {...props} />;
    case ENUMS.JobFreelancerStatus.APPLICATION_SENT:
      return (
        <ApplicationSentTile
          job={job}
          bid={bid}
          subheader={`Applied ${appliedDateFromNow}`}
          {...props}
        />
      );
    case ENUMS.JobFreelancerStatus.IN_REVIEW:
      return (
        <ApplicationSentTile
          subheader="In review"
          job={job}
          bid={bid}
          {...props}
        />
      );
    case ENUMS.JobFreelancerStatus.LIVE_INTERVIEWING:
      return <LiveInterviewingTile job={job} {...props} />;
    case ENUMS.JobFreelancerStatus.AI_INTERVIEWING:
      if (!bid) return null;
      return <AIInterviewingTile job={job} bidId={bid.id} {...props} />;
    case ENUMS.JobFreelancerStatus.IN_REVIEW_AFTER_AI_INTERVIEW:
      return (
        <InReviewAfterAIInterviewTile
          jobId={job.id}
          bidId={bid?.id}
          {...props}
        />
      );
    case ENUMS.JobFreelancerStatus.PENDING_BANK_INFORMATION:
      return <PendingBankInfoTile job={job} {...props} />;
    case ENUMS.JobFreelancerStatus.NEW_OFFER:
    case ENUMS.JobFreelancerStatus.UPDATED_OFFER: {
      if (userFailedIdentityVerification) {
        return (
          <NotSelectedTile
            header="Verification failed"
            subheader="Identity verification is required."
            jobId={job.id}
            {...props}
          />
        );
      }

      return <NewOfferTile job={job} {...props} />;
    }
    case ENUMS.JobFreelancerStatus.OFFER_ACCEPTED:
    case ENUMS.JobFreelancerStatus.OFFER_EXTENDED:
      return <HiredTile job={job} {...props} />;
    case ENUMS.JobFreelancerStatus.APPLICATION_REJECTED:
      return (
        <NotSelectedTile
          header="Not selected"
          jobId={job.id}
          subheader="The client decided to not move forward with your application."
          {...props}
        />
      );
    case ENUMS.JobFreelancerStatus.JOB_FILLED:
      return <NotSelectedTile header="Job filled" jobId={job.id} {...props} />;
    case ENUMS.JobFreelancerStatus.JOB_CLOSED:
      return <NotSelectedTile header="Closed" jobId={job.id} {...props} />;
    case ENUMS.JobFreelancerStatus.JOB_ON_HOLD:
      return <NotSelectedTile header="On hold" jobId={job.id} {...props} />;
    case ENUMS.JobFreelancerStatus.NON_SUPPORTED_LOCATION:
      return (
        <NotSelectedTile
          header="Non supported location"
          subheader="This job doesn't support payments in your location."
          jobId={job.id}
          {...props}
        />
      );

    default: {
      assertUnreachable(statusForFreelancer);
      return null;
    }
  }
};

type BaseTileProps = {
  className?: string;
  longTile?: boolean;
};

type ApplyTileProps = BaseTileProps & {
  job: Job;
  bid?: FreelancerBidListItem;
};

const ApplyTile = ({ job, bid, ...props }: ApplyTileProps) => {
  const { longTile } = props;
  return (
    <JobStatusBox color="var(--positive-1)" {...props}>
      <JobStatusBox.Header {...props}>
        Accepting applications
      </JobStatusBox.Header>
      <JobStatusBox.Content {...props}>
        <BidButton
          fullWidth={!longTile}
          variant="positive"
          shape="squared"
          job={job}
          bid={bid}
        />
      </JobStatusBox.Content>
    </JobStatusBox>
  );
};

type InvitedTileProps = ApplyTileProps & {
  label: string;
};

const InvitedTile = ({ label, job, bid, ...props }: InvitedTileProps) => {
  const { longTile } = props;
  return (
    <JobStatusBox color="var(--medium-violet)" {...props}>
      <JobStatusBox.Header color="primary" {...props}>
        {label}
      </JobStatusBox.Header>
      <JobStatusBox.Content {...props}>
        <BidButton
          fullWidth={!longTile}
          variant="primary"
          shape="squared"
          job={job}
          bid={bid}
        />
      </JobStatusBox.Content>
    </JobStatusBox>
  );
};

type PendingTileProps = BaseTileProps & {
  bidId?: number;
  jobId: number;
};

const PendingTile = ({ bidId, jobId, ...props }: PendingTileProps) => {
  const { longTile } = props;

  return (
    <JobStatusBox color="var(--dark-blue)" {...props}>
      <div>
        <JobStatusBox.Header>Additional Steps Needed</JobStatusBox.Header>
        <JobStatusBox.Subheader>
          Please complete the required steps shown on your Home page to continue
          your application.
        </JobStatusBox.Subheader>
      </div>
      <JobStatusBox.Content {...props}>
        <PendingFreelancerActionButton
          fullWidth={!longTile}
          variant="blue"
          shape="squared"
        />
        <ViewApplicationButton
          bidId={bidId}
          jobId={jobId}
          fullWidth={!longTile}
          variant="secondary"
          inverse
          shape="squared"
        />
      </JobStatusBox.Content>
    </JobStatusBox>
  );
};

type ApplicationSentTileProps = BaseTileProps & {
  subheader: string;
  bid: FreelancerBidListItem | undefined;
  job: Job;
};

const ApplicationSentTile = ({
  subheader,
  bid,
  job,
  ...props
}: ApplicationSentTileProps) => {
  const bidId = bid?.id;
  const jobId = job.id;

  if (!bidId) return null;

  const { longTile } = props;

  return (
    <JobStatusBox color="var(--dark-blue)" {...props}>
      <div>
        <JobStatusBox.Header {...props}>Application sent</JobStatusBox.Header>
        <JobStatusBox.Subheader>{subheader}</JobStatusBox.Subheader>
      </div>
      <JobStatusBox.Content {...props}>
        <ViewApplicationButton
          jobId={jobId}
          bidId={bidId}
          variant="blue"
          fullWidth={!longTile}
          shape="squared"
        />
      </JobStatusBox.Content>
    </JobStatusBox>
  );
};

const LiveInterviewingTile = ({
  job,
  longTile,
  ...props
}: Omit<ApplyTileProps, "bid">) => {
  return (
    <JobStatusBox color="var(--dark-teal)" {...props}>
      <div>
        <JobStatusBox.Header {...props}>Interviewing</JobStatusBox.Header>
        <JobStatusBox.Subheader>
          You’ve been invited to schedule a live video interview.
        </JobStatusBox.Subheader>
      </div>
      <JobStatusBox.Content {...props}>
        <ScheduleInterviewButton
          employerUserId={job.creator_user_id}
          fullWidth={!longTile}
          variant="medium-teal"
          shape="squared"
        />
      </JobStatusBox.Content>
    </JobStatusBox>
  );
};

type AIInterviewingTileProps = ApplyTileProps & {
  bidId: number;
};

const AIInterviewingTile = ({
  job,
  bidId,
  ...props
}: AIInterviewingTileProps) => {
  const { longTile } = props;
  return (
    <JobStatusBox color="var(--dark-teal)" {...props}>
      <div>
        <JobStatusBox.Header {...props}>Interviewing</JobStatusBox.Header>
        <JobStatusBox.Subheader>
          You’ve been invited to an AI interview. You can start it at any time.
        </JobStatusBox.Subheader>
      </div>
      <JobStatusBox.Content {...props}>
        <StartAIInterviewButton
          variant="medium-teal"
          bidId={bidId}
          fullWidth={!longTile}
          shape="squared"
        />
      </JobStatusBox.Content>
    </JobStatusBox>
  );
};

type NewOfferTileProps = Omit<ApplyTileProps, "bid">;

const NewOfferTile = ({ job, ...props }: NewOfferTileProps) => {
  const { longTile } = props;

  if (!job?.offer_id) return null;

  return (
    <div className={styles.newOfferTile}>
      <JobStatusBox
        color="var(--positive-2)"
        sx={{
          border: "4px solid var(--white)",
          position: "relative",
          overflow: "hidden",
        }}
        {...props}
      >
        <>
          <NewOfferBg color="var(--soft-green)" className={styles.bg} />
          <Box sx={{ zIndex: 1 }}>
            <JobStatusBox.Header {...props}>New offer</JobStatusBox.Header>
            <JobStatusBox.Subheader>
              Review your job offer
            </JobStatusBox.Subheader>
          </Box>
          <JobStatusBox.Content {...props}>
            <RespondToOfferButton
              jobId={job.id}
              offerId={job.offer_id}
              fullWidth={!longTile}
              variant="primary"
              shape="squared"
              inverse
            />
          </JobStatusBox.Content>
        </>
      </JobStatusBox>
    </div>
  );
};

type PendingBankInfoTileProps = Omit<ApplyTileProps, "bid">;

const PendingBankInfoTile = ({ job, ...props }: PendingBankInfoTileProps) => {
  const handleAddInfo = () => {
    openChoosePayoutMethodModal();
  };
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "16px",
        border: "3px solid var(--negative-2)",
      }}
    >
      <JobStatusBox
        color="var(--negative-2)"
        sx={{
          border: "4px solid var(--white)",
          position: "relative",
          overflow: "hidden",
        }}
        {...props}
      >
        <>
          <Box sx={{ zIndex: 1 }}>
            <JobStatusBox.Header {...props}>
              Pending bank information
            </JobStatusBox.Header>
          </Box>
          <JobStatusBox.Content {...props}>
            <Button
              shape="squared"
              variant="destructive"
              fullWidth={!props.longTile}
              onClick={handleAddInfo}
            >
              Add now
            </Button>
          </JobStatusBox.Content>
        </>
      </JobStatusBox>
    </Box>
  );
};

type HiredTileProps = Omit<ApplyTileProps, "bid">;

const HiredTile = ({ job, ...props }: HiredTileProps) => {
  const { longTile } = props;

  if (!job?.offer_id) return null;

  return (
    <JobStatusBox color="var(--positive-3)" {...props}>
      <div>
        <JobStatusBox.Header color="positive" {...props}>
          You’re hired 🎉
        </JobStatusBox.Header>
        <JobStatusBox.Subheader color="positive">
          You’ve accepted the offer
        </JobStatusBox.Subheader>
      </div>
      <JobStatusBox.Content {...props}>
        <ReviewContractButton
          jobId={job.id}
          offerId={job.offer_id}
          fullWidth={!longTile}
          variant="positive-1"
          shape="squared"
        />
      </JobStatusBox.Content>
    </JobStatusBox>
  );
};

type NotSelectedTileProps = BaseTileProps & {
  header: string;
  jobId: number;
  subheader?: string;
};

const NotSelectedTile = ({
  header,
  jobId,
  subheader,
  ...props
}: NotSelectedTileProps) => {
  const { longTile } = props;

  return (
    <JobStatusBox color="var(--black)" {...props}>
      <div>
        <JobStatusBox.Header {...props}>{header}</JobStatusBox.Header>
        <JobStatusBox.Subheader>
          {subheader || "Not accepting applications"}
        </JobStatusBox.Subheader>
      </div>
      <JobStatusBox.Content {...props}>
        <BrowseOpenJobs inverse shape="squared" fullWidth={!longTile} />
      </JobStatusBox.Content>
    </JobStatusBox>
  );
};

type InReviewAfterAIInterviewTileProps = BaseTileProps & {
  jobId: number;
  bidId?: number;
};

const InReviewAfterAIInterviewTile = ({
  jobId,
  bidId,
  longTile,
  ...props
}: InReviewAfterAIInterviewTileProps) => {
  return (
    <JobStatusBox color="var(--dark-teal)" {...props}>
      <JobStatusBox.Header>Interviewing</JobStatusBox.Header>
      <JobStatusBox.Subheader>
        You’ve completed your AI interview and it is being reviewed.
      </JobStatusBox.Subheader>
      <JobStatusBox.Content>
        <ViewApplicationButton
          variant="medium-teal"
          jobId={jobId}
          bidId={bidId}
          fullWidth={!longTile}
          shape="squared"
        />
      </JobStatusBox.Content>
    </JobStatusBox>
  );
};
